import React, { useMemo } from "react";

import { Tooltip } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useOperationDespatchContext } from "../../../../../../../../contexts/DespatchContext";
import {
  EquipmentIcons,
  FleetTypeIconEquipmentDicitonaryType,
  GeneralTypeIconEquipmentDicitonaryType,
} from "Components/Generic/Icons/Equipment";
import { Fleet } from "Models/fleet";
import { MemberAvailability } from "Models/members/member.availability";
import { useTranslation } from "react-i18next";
import { checkResourceAvailability } from "Util/checkAvailability";
import { EquipmentType } from "Util/enums";
import { getEquipmentName } from "Util/getEquipmentName";

import {
  Card,
  CardIcon,
  CardInfosWrapper,
  Details,
  PlainText,
  SubTitle,
  UnavailableText,
} from "../styles";
import { SubContractorBadge } from "../SubContractorBadge";
import { useAppState } from "Context/StateContext";

interface ResourceEquipmentCardProps {
  tasks?: { id: number; type: string; jobReference: string }[];
  isSubcontractor: boolean;
  actualDate: Date;
  availabilities?: MemberAvailability[];
  equipmentType?: string;
  description?: string;
  fleetNumber?: string;
  equipment: Fleet;
  crewNamesRelated?: string[];
}

export const ResourceEquipmentCard: React.FC<ResourceEquipmentCardProps> = ({
  isSubcontractor,
  actualDate,
  availabilities,
  equipment,
  crewNamesRelated,
  tasks,
}) => {
  const { t: translate } = useTranslation();
  const { formatWithUserTimezone, userTimezone } = useAppState();
  const { showUnavailableItems } = useOperationDespatchContext();

  const result = checkResourceAvailability(actualDate, availabilities, userTimezone?.name);

  const numberOfTasks = useMemo(() => {
    return tasks
      ? `${tasks.length} ${tasks.length === 1 ? translate("task") : translate("tasks")}`
      : `${translate("notAllocated")}`;
  }, [tasks]);

  const tasksDescriptions = useMemo(() => {
    return tasks?.map((task) => `#${task.jobReference} - ${task.type}`).join(", ");
  }, [tasks]);

  if (!showUnavailableItems && !result.isAvailable) {
    return null;
  }
  const { title, text } = getEquipmentName(equipment, translate("general"), translate("fleet"));

  const renderTask = () => {
    if (result.unavailableHours) {
      return (
        <UnavailableText isAvailable={result.isAvailable}>
          {translate("unavailableOn")} {result.unavailableHours.date} {translate("between")}{" "}
          {result.unavailableHours.from} {translate("and")} {result.unavailableHours.to}
        </UnavailableText>
      );
    }

    if (result.isAvailable) {
      if (crewNamesRelated?.length) {
        return (
          <Tooltip title={tasksDescriptions} disableInteractive>
            <SubTitle>{crewNamesRelated.join(", ")}</SubTitle>
          </Tooltip>
        );
      }

      return (
        <Tooltip title={tasksDescriptions} disableInteractive>
          <SubTitle isEmpty={!tasks || tasks.length === 0}>{numberOfTasks}</SubTitle>
        </Tooltip>
      );
    }

    return (
      <UnavailableText>{`${translate("unavailableUntil")} ${
        !!result.nextAvailable && formatWithUserTimezone(result.nextAvailable, "dd MMM")
      }`}</UnavailableText>
    );
  };

  return (
    <Card sx={{ color: colors.gray600 }}>
      <CardIcon>
        <EquipmentIcons
          color={colors.gray600}
          name={
            equipment.iconName as
              | FleetTypeIconEquipmentDicitonaryType
              | GeneralTypeIconEquipmentDicitonaryType
          }
          size={24}
          typeOfEquipment={
            equipment.equipmentType === EquipmentType.FLEET
              ? EquipmentType.FLEET
              : EquipmentType.GENERAL
          }
        />
      </CardIcon>

      <CardInfosWrapper>
        <Details>
          <PlainText sx={{ display: "flex", gap: "4px" }}>
            <strong style={{ fontWeight: 600 }}>{title}</strong>

            <span>{text}</span>
          </PlainText>
          {renderTask()}
        </Details>
        {!!isSubcontractor && <SubContractorBadge />}
      </CardInfosWrapper>
    </Card>
  );
};
